/**
 * `SheetRowReads` — a registered row's metadata as the client's reads hand it
 * out: `client.rowRegistry.peek(id)` / `iter()`, `handle.sheetRow(id)` and the
 * row `platformOf(client).registerRow` registered.
 *
 * The object behind it is the live `SheetRow` in the client's row registry,
 * which the SDK rewrites (a label patch, a descriptor override, a stored
 * height): each member reads the live row, so the face always answers the
 * current metadata. The face itself is frozen and carries no writer and no
 * wire record (`raw`): nothing read off it writes the registry, the row's
 * descriptor or its attribute bag — not through a cast, an `instanceof` or a
 * prototype (it is a plain object, no `SheetRow`). Built once per row by the
 * client's `SheetRowFaces`, so a client's reads of the same registered row
 * compare by identity.
 *
 * The wire record behind a row is a white-box read
 * (`clientInstrumentationOf(client).rowRegistry.raw(id)`,
 * `@tento-lona/internal-testing`).
 */

import type { Option, RowLocalId } from "@tento-lona/sheets";
import type { Json, JsonObject } from "@tento-lona/sheets";
import type { SheetRow } from "./sheet-row.ts";

/** A registered row's reads — the element `client.rowRegistry` hands out. */
export type SheetRowReads = Readonly<
  Pick<
    SheetRow,
    | "id"
    | "label"
    | "lookupKey"
    | "wireType"
    | "heightPx"
    | "descriptor"
    | "displayLabel"
    | "attributes"
    | "attributesAs"
    | "capabilities"
  >
>;

/** `value`, deeply frozen: the JSON a face hands out cannot be written through. */
function deepFreeze(value: Json): Json {
  if (typeof value !== "object" || value === null) return value;
  for (const member of Object.values(value)) deepFreeze(member);
  return Object.freeze(value);
}

/**
 * A client's row faces: each registered row's one frozen `SheetRowReads`,
 * and the frozen copy of each attribute bag a face handed out. Owned by the
 * client (its row registry's reads), so the faces live and die with it.
 */
export class SheetRowFaces {
  /** Every live row's face: one per row, so reads compare by identity. */
  readonly #faces = new WeakMap<SheetRow, SheetRowReads>();
  /** Each attribute bag's frozen copy, keyed by the row's (cached) bag. */
  readonly #bags = new WeakMap<JsonObject, JsonObject>();

  /**
   * The frozen reads face of the registered row `row` — see
   * {@link SheetRowReads}. Built on first read and cached per row.
   */
  of(row: SheetRow): SheetRowReads {
    const known = this.#faces.get(row);
    if (known != null) return known;
    const face = faceOf(row, (bag) => this.#frozenBag(bag));
    this.#faces.set(row, face);
    return face;
  }

  /** Every `[id, row]` entry of `rows` with its row as its reads face (`of`), in order. */
  *entries(rows: Iterable<[RowLocalId, SheetRow]>): IterableIterator<[RowLocalId, SheetRowReads]> {
    for (const [id, row] of rows) yield [id, this.of(row)];
  }

  /** A frozen copy of `bag`, made once per bag (a row re-reads its bag after a write). */
  #frozenBag(bag: Option<JsonObject>): Option<JsonObject> {
    if (bag == null) return null;
    const known = this.#bags.get(bag);
    if (known != null) return known;
    const copy: JsonObject = structuredClone(bag);
    deepFreeze(copy);
    this.#bags.set(bag, copy);
    return copy;
  }
}

/** `row`'s face, its attribute reads frozen through `frozenBag`. */
function faceOf(
  row: SheetRow,
  frozenBag: (bag: Option<JsonObject>) => Option<JsonObject>,
): SheetRowReads {
  return Object.freeze({
    id: row.id,
    get label() {
      return row.label;
    },
    get lookupKey() {
      return row.lookupKey;
    },
    get wireType() {
      return row.wireType;
    },
    get heightPx() {
      return row.heightPx;
    },
    get descriptor() {
      return row.descriptor;
    },
    get displayLabel() {
      return row.displayLabel;
    },
    attributes: () => frozenBag(row.attributes()),
    attributesAs: <A>(decode: (attributes: JsonObject) => Option<A>): Option<A> => {
      const bag = frozenBag(row.attributes());
      return bag == null ? null : decode(bag);
    },
    capabilities: () => Object.freeze(row.capabilities()),
  });
}
